<template>
  <div>
    <section class="space-y-3">
      <p
        class="text-text-neutral-tertiary flex items-center justify-between text-xs"
      >
        <span>{{ t('general.passengers') }}</span>
        <span>{{ validPassengersAmount }}</span>
      </p>
      <section class="space-y-4">
        <MultiplePassengerBlock
          v-for="(key, idx) in passengerKeys"
          :key="key"
          :pax-idx="idx"
          :schema="schema[key]"
          :passenger-key="key"
          :passenger="getTypedPassenger(key)"
          :saved-passengers="savedPassengers"
          :is-domestic="isDomestic"
          :provider-class="providerClass"
          :departure-date="departureDate"
          @onPassengerUpdate="emit('onPassengerUpdate', $event)"
          @onOpenSavedPassengersSheet="
            handleOpenSavedPassengersSheet(key, $event)
          "
        />
      </section>
    </section>
    <!--
      Bottom sheets and modals are siblings of the `space-y-3` section so
      Tailwind's sibling margin (`* + *`) doesn't apply to them. That margin
      offsets `position: fixed` elements that set both `top` and `bottom`
      (the overlay's `fixed inset-0`), creating a visible gap at the top of
      the sheet.

      Order matters: all sheets share the same z-index, so DOM order decides
      stacking. The saved-passengers sheet is rendered first because every
      other sheet (add method, photo flow, manual form) opens on top of it.
    -->
    <SavedPassengersBottomSheet
      :name="savedPassengersBottomSheetName"
      :passengers="visibleSavedPassengers"
      :passenger-on-card="activeSelectedPassenger"
      :passenger-type="activePassengerType"
      :is-loading="accountData.isLoading.value"
      :provider-class="providerClass"
      @onPassengerUpdate="handlePassengerUpdate"
      @onPassengerFormOpen="handlePassengerFormOpen"
      @onAddNewPassenger="handleAddNewPassengerRequest"
    />
    <PassengerAddMethodBottomSheet
      v-if="activePassengerKey"
      :name="passengerAddMethodBottomSheetName"
      :passenger-type="activePassengerType"
      @onManual="onAddMethodManual"
      @onPhoto="onAddMethodPhoto"
    />
    <PassengerDocumentPhotoBottomSheet
      :name="passengerDocumentPhotoBottomSheetName"
      :api-client="api.client"
      @onAutofillFlowComplete="handleDocumentAutofillComplete"
      @onEditRequest="handleDocumentAutofillEdit"
      @onPublicOfferClick="bottomSheet.show(docScanPublicOfferBsName)"
    />
    <PassengerFormBottomSheet
      v-if="activePassengerKey && activeSchema"
      :name="passengerFormBottomSheetName"
      :saved-passengers-bottom-sheet-name="savedPassengersBottomSheetName"
      :all-passengers="combinedPassengers"
      :passenger-idx="passengerIdxToEdit"
      :passenger-on-card="
        passengerOnCardForForm || formPassengerConfig.emptyPassenger
      "
      :passenger-key="activePassengerKey"
      :schema="activeSchema"
      :is-domestic="isDomestic"
      :provider-class="providerClass"
      :is-new-passenger="isNewPassenger"
      @onPassengerEdit="handlePassengerEdit"
      @onPassengerCreate="handlePassengerCreate"
      :on-edit-callback="handleEditCallback"
    />
    <!--
      Teleport to body so the alerts are rendered outside this component's
      stacking context. Without Teleport the inner `position: absolute` of
      FreeAlert overrides the `fixed` class from here (CSS order: `absolute`
      is declared after `fixed` in Tailwind output, so it wins). Teleporting
      ensures `fixed` takes effect relative to the viewport, keeping the
      toast visible over bottom sheets (z-1000) and stable during page scroll.
    -->
    <Teleport to="body">
      <FreeAlert
        ref="editSuccessAlertRef"
        class="fixed inset-x-4 bottom-28 z-1100 flex"
        :duration="4000"
      >
        <template #icon>
          <img
            src="@/assets/images/round-checkmark-icon.svg"
            width="24"
            height="24"
            alt=""
          />
        </template>
        <template #default="{ content }">
          <p class="text-text-neutral-primary">
            {{ content }}
          </p>
        </template>
      </FreeAlert>
      <FreeAlert
        ref="editErrorAlertRef"
        class="fixed inset-x-4 bottom-28 z-1100 flex"
        :duration="4000"
      >
        <template #icon>
          <img
            src="@/assets/images/red-warning.svg"
            width="24"
            height="24"
            alt=""
          />
        </template>
        <template #default>
          <div>
            <p class="text-text-neutral-primary">
              {{ t('passengersData.editAlert.errorTitle') }}
            </p>
            <p class="text-text-neutral-tertiary text-xs">
              {{ t('passengersData.editAlert.errorSubtitle') }}
            </p>
          </div>
        </template>
      </FreeAlert>
    </Teleport>
    <PublicOfferBottomSheet
      :name="docScanPublicOfferBsName"
      :fetch-public-offer="publicOfferApi.fetchPublicOffer"
    />
    <MissingPassengerDataModal
      :modal-name="missingPassengerDataModalName"
      @onAgree="handleMissingData"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useVuelidate } from '@vuelidate/core';
  import { v4 as uuid } from 'uuid';
  import { storeToRefs } from 'pinia';
  import type { Airflow } from '@aviata/platform-types';
  import type {
    PassengersData,
    PassengerKey,
    Passenger,
  } from '@aviata/booking-data';
  import { controllerModel, providerClassLib } from 'shared';
  import {
    FreeAlert,
    PublicOfferBottomSheet,
    useFirebaseStore,
  } from 'freedom-shared';

  import {
    savedPassengersConfig,
    SavedPassengersBottomSheet,
  } from '@/features/saved-passengers';

  import { publicOfferApi } from '@/entities/public-offer';
  import { accountModel } from '@/entities/account';
  import {
    formPassengerLib,
    formPassengerConfig,
  } from '@/entities/form-passenger';

  import { api } from '@/shared/api';
  import { analyticsEventsLib } from '@/shared/lib';

  import {
    filterSavedPassengersForKey,
    isSameActivePassenger,
    localPassengersStorage,
  } from '../lib';
  import {
    PASSENGER_FORM_BOTTOM_SHEET,
    PASSENGER_ADD_METHOD_BOTTOM_SHEET,
    PASSENGER_DOCUMENT_PHOTO_BOTTOM_SHEET,
  } from '../config';
  import {
    useDocScanFlow,
    useEditResultAlerts,
    usePassengersForm,
    useSkipEmptySavedList,
  } from '../model';
  import type { PassengerWithDocType } from '../types';
  import MultiplePassengerBlock from './MultiplePassengerBlock.vue';
  import PassengerFormBottomSheet from './PassengerFormBottomSheet.vue';
  import PassengerAddMethodBottomSheet from './PassengerAddMethodBottomSheet.vue';
  import PassengerDocumentPhotoBottomSheet from './PassengerDocumentPhotoBottomSheet.vue';
  import MissingPassengerDataModal from './MissingPassengerDataModal.vue';

  const props = defineProps<{
    isDomestic: boolean;
    passengers: PassengersData;
    passengerKeys: PassengerKey[];
    schema: Airflow.Offer.Schema.Passengers;
    providerClass?: string;
    departureDate?: string;
    /**
     * Skip the on-mount auto-open of the saved-passengers sheet for the
     * first empty passenger. QuickBookingPage hosts this widget purely for
     * its nested bottom-sheets and opens them on demand via `openSavedList`,
     * so the implicit "pop the sheet open right after the page paints"
     * behavior is unwanted there.
     */
    disableAutoOpen?: boolean;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [
      updatedPassenger: { key: PassengerKey; passenger: Passenger },
    ];
  }>();

  const { t } = useI18n();
  const validations = useVuelidate();
  const bottomSheet = controllerModel.useBottomSheet();

  const { serializedRemoteConfig } = storeToRefs(useFirebaseStore());
  const isPhotoEnabled = computed(
    () =>
      serializedRemoteConfig.value
        ?.avfree_pax_data_auto_filling_by_photo_enabled === true,
  );
  const modal = controllerModel.useModal();

  const accountData = accountModel.useAccount();

  const {
    savedPassengers,
    localPassengers,
    combinedPassengers,
    getLocalPassengers,
    handleSavedPassengerUpdate,
    updateLocalPassenger,
    activePassengerKey,
    activeSelectedPassenger,
    passengerIdxToEdit,
    passengerOnCardForForm,
    isNewPassenger,
    activePassengerType,
    activeSchema,
    checkIfPassengerHasRequiredDocumentFields,
  } = usePassengersForm(props.schema);

  // Bottom sheet state
  const savedPassengersBottomSheetName =
    savedPassengersConfig.SAVED_PASSENGERS_BOTTOM_SHEET;
  const passengerAddMethodBottomSheetName =
    PASSENGER_ADD_METHOD_BOTTOM_SHEET + uuid();
  const passengerDocumentPhotoBottomSheetName =
    PASSENGER_DOCUMENT_PHOTO_BOTTOM_SHEET + uuid();
  const passengerFormBottomSheetName = PASSENGER_FORM_BOTTOM_SHEET + uuid();
  const missingPassengerDataModalName = `missing-passenger-data-modal-${uuid()}`;
  const docScanPublicOfferBsName = `doc-scan-public-offer-${uuid()}`;
  const isBottomSheetTransitioning = ref(false);
  const isEditingActivePassenger = ref(false);

  const visibleSavedPassengers = computed(() =>
    filterSavedPassengersForKey(combinedPassengers.value, {
      isDomestic: props.isDomestic,
      ageBetween: activePassengerKey.value
        ? props.schema[activePassengerKey.value]?.validators.age_between
        : undefined,
      departureDate: props.departureDate,
    }),
  );

  const handleOpenSavedPassengersSheet = (
    key: PassengerKey,
    selectedPassenger: Passenger | null,
  ) => {
    analyticsEventsLib.savedPaxListOpen({
      key,
      providerClass: props.providerClass,
    });
    activePassengerKey.value = key;
    activeSelectedPassenger.value = selectedPassenger;
    localPassengers.value = getLocalPassengers();
    bottomSheet.show(savedPassengersBottomSheetName);
  };

  const handlePassengerFormOpen = (
    passengerOrNull: Passenger | null,
    prefilledPassenger?: Passenger,
  ) => {
    const idx: number | null =
      passengerOrNull !== null
        ? (() => {
            const found = combinedPassengers.value.findIndex(p => {
              const pWithId = p as PassengerWithDocType;
              const targetWithId = passengerOrNull as PassengerWithDocType;
              return targetWithId.id
                ? pWithId.id === targetWithId.id
                : isSameActivePassenger(p, passengerOrNull);
            });
            return found >= 0 ? found : null;
          })()
        : null;

    isNewPassenger.value = idx === null;
    passengerIdxToEdit.value = idx;
    isBottomSheetTransitioning.value = true;

    if (idx !== null && activePassengerKey.value) {
      const passengerToEdit = combinedPassengers.value[idx];
      const previousSelectedPassenger = activeSelectedPassenger.value;
      const currentPassenger = getTypedPassenger(activePassengerKey.value);
      passengerOnCardForForm.value = passengerToEdit;
      // Track whether the edited passenger is the one currently selected on
      // the card. Don't mutate `activeSelectedPassenger` here — that would
      // make a "just open the editor" action silently change the selection.
      isEditingActivePassenger.value =
        isSameActivePassenger(passengerToEdit, previousSelectedPassenger) ||
        isSameActivePassenger(passengerToEdit, currentPassenger);
    } else {
      passengerOnCardForForm.value =
        prefilledPassenger ?? formPassengerConfig.emptyPassenger;
      isEditingActivePassenger.value = true;
    }

    // Intentionally keep the saved-passengers sheet open behind the form so
    // closing the form drops the user back into the saved list instead of
    // the page.
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
    bottomSheet.show(passengerFormBottomSheetName);
    nextTick(() => {
      isBottomSheetTransitioning.value = false;
    });
  };

  const handleAddNewPassengerRequest = () => {
    if (!isPhotoEnabled.value) {
      handlePassengerFormOpen(null);
      return;
    }
    isBottomSheetTransitioning.value = true;
    nextTick(() => {
      bottomSheet.show(passengerAddMethodBottomSheetName);
      nextTick(() => {
        isBottomSheetTransitioning.value = false;
      });
    });
  };

  const onAddMethodPhoto = () => {
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.show(passengerDocumentPhotoBottomSheetName);
  };

  const handlePassengerUpdate = (updated: Passenger) => {
    if (!activePassengerKey.value) {
      return;
    }

    // Проверяем обязательные поля документа только для FunSun
    // и только если пассажир выбран из списка сохраненных (имеет id)
    // Новые пассажиры с заполненными SM полями не должны вызывать модальное окно,
    // так как они могут быть сохранены позже в PassengerFormBottomSheet
    const isPassengerFromSavedList = !!(updated as PassengerWithDocType).id;
    const shouldCheckDocumentFields =
      providerClassLib.isFunSun(props.providerClass) &&
      isPassengerFromSavedList;

    if (
      shouldCheckDocumentFields &&
      !checkIfPassengerHasRequiredDocumentFields(
        updated as PassengerWithDocType,
      )
    ) {
      passengerOnCardForForm.value = updated;
      activeSelectedPassenger.value = updated;
      modal.show(missingPassengerDataModalName);
    } else {
      bottomSheet.hide(savedPassengersBottomSheetName);
      bottomSheet.hide(passengerAddMethodBottomSheetName);
      bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
      activeSelectedPassenger.value = updated;
      emit('onPassengerUpdate', {
        key: activePassengerKey.value,
        passenger: updated,
      });
    }
  };

  const onAddMethodManual = () => {
    handlePassengerFormOpen(null);
  };

  const { handleDocumentAutofillComplete, handleDocumentAutofillEdit } =
    useDocScanFlow({
      activePassengerKey,
      refreshLocalPassengers: () => {
        localPassengers.value = getLocalPassengers();
      },
      onPassengerSelected: handlePassengerUpdate,
      onFormOpenWithPrefill: (prefilledPassenger?: Passenger) =>
        handlePassengerFormOpen(null, prefilledPassenger),
    });

  const handleMissingData = () => {
    const passengerId = (passengerOnCardForForm.value as PassengerWithDocType)
      .id;
    const idx = passengerId
      ? combinedPassengers.value.findIndex(pax => {
          const savedPax = pax as PassengerWithDocType;
          return savedPax.id === passengerId;
        })
      : -1;

    passengerIdxToEdit.value = idx >= 0 ? idx : null;
    bottomSheet.hide(passengerAddMethodBottomSheetName);
    bottomSheet.hide(passengerDocumentPhotoBottomSheetName);
    bottomSheet.show(passengerFormBottomSheetName);
  };

  const handlePassengerCreate = (newPax: Passenger) => {
    if (!activePassengerKey.value) {
      return;
    }

    // `handlePassengerCreate` fires twice for a new passenger — once with
    // the temp uuid (already in the local list, written by the form sheet)
    // and once with the backend id after the mutation lands. The upsert
    // matches the row by non-empty doc number and rebinds the id; the
    // subsequent `getLocalPassengers()` re-reads + dedups for the UI.
    const passengerWithBackendId = newPax as PassengerWithDocType;
    if (passengerWithBackendId.id) {
      localPassengersStorage.upsert(passengerWithBackendId);
    }

    localPassengers.value = getLocalPassengers();
    passengerIdxToEdit.value = combinedPassengers.value.length - 1;
    passengerOnCardForForm.value = newPax;
    activeSelectedPassenger.value = newPax;
    // Новый пассажир всегда обновляется напрямую без проверки SM полей,
    // так как он был только что создан и может быть дополнен позже
    isNewPassenger.value = false;
    emit('onPassengerUpdate', {
      key: activePassengerKey.value,
      passenger: newPax,
    });
  };

  const handlePassengerEdit = (updated: Passenger, idx: number) => {
    if (!activePassengerKey.value) {
      return;
    }

    if (idx < savedPassengers.value.length) {
      handleSavedPassengerUpdate(idx, updated);
    } else {
      updateLocalPassenger(idx, updated);
    }

    // Propagate the edit to the card only when the user was editing the
    // currently-selected passenger. Otherwise leave the active selection
    // alone so editing a non-selected row doesn't silently swap who's on
    // the card.
    if (isEditingActivePassenger.value) {
      passengerOnCardForForm.value = updated;
      activeSelectedPassenger.value = updated;
      isEditingActivePassenger.value = false;
      emit('onPassengerUpdate', {
        key: activePassengerKey.value,
        passenger: updated,
      });
    }

    passengerIdxToEdit.value = idx;
  };

  const {
    editSuccessAlertRef,
    editErrorAlertRef,
    handleEditSuccess,
    handleEditError,
  } = useEditResultAlerts();

  const handleEditCallback = ({
    success,
    passenger,
  }: {
    success: boolean;
    passenger?: Passenger;
  }) => {
    if (success && passenger) {
      handleEditSuccess(passenger);
    } else {
      handleEditError();
    }
  };

  /**
   * Need to use type casting since we need to check if given key is present in passengers.
   * The given keys are always present according to the business logic, so we need to either add a
   * type guard that guarantees it or use type casting
   *
   * TODO: Add type guard that guarantees that Passenger is not undefined
   */
  function getTypedPassenger(key: PassengerKey) {
    return props.passengers[key] as Passenger;
  }

  const validPassengersAmount = computed(() => {
    const validAmount = props.passengerKeys
      .filter(passengerKey => props.passengers[passengerKey].firstName)
      .map(validations.value.$getResultsForChild)
      .filter(validation => (validation ? !validation.$invalid : false)).length;

    return t('passengersData.validPassengersAmount', {
      value: validAmount,
      total: props.passengerKeys.length,
    });
  });

  const handleAllBottomSheetsClose = () => {
    activePassengerKey.value = null;
  };

  watch(
    () => accountData.data.value?.passengers,
    passengers => {
      if (passengers && Array.isArray(passengers)) {
        savedPassengers.value = passengers.map(
          formPassengerLib.mapSavedPassenger,
        );
        localPassengers.value = getLocalPassengers();
      }
    },
    { immediate: true },
  );

  watch(
    () => savedPassengers.value,
    () => {
      localPassengers.value = getLocalPassengers();
    },
  );

  watch(bottomSheet.state, () => {
    const isAnySheetVisible =
      bottomSheet.isVisible(savedPassengersBottomSheetName) ||
      bottomSheet.isVisible(passengerFormBottomSheetName) ||
      bottomSheet.isVisible(passengerAddMethodBottomSheetName) ||
      bottomSheet.isVisible(passengerDocumentPhotoBottomSheetName);
    if (!isBottomSheetTransitioning.value && !isAnySheetVisible) {
      handleAllBottomSheetsClose();
    }
  });

  useSkipEmptySavedList({
    savedSheetName: savedPassengersBottomSheetName,
    accountData,
    visibleSavedPassengers,
    onAddNewPassenger: handleAddNewPassengerRequest,
  });

  onMounted(() => {
    localPassengers.value = getLocalPassengers();
    if (props.disableAutoOpen) {
      return;
    }
    const firstKey = props.passengerKeys[0];
    if (firstKey && !getTypedPassenger(firstKey).firstName) {
      nextTick(() => {
        handleOpenSavedPassengersSheet(firstKey, getTypedPassenger(firstKey));
      });
    }
  });

  defineExpose({
    openSavedList(key: PassengerKey) {
      handleOpenSavedPassengersSheet(key, getTypedPassenger(key));
    },
  });
</script>
